import clsx from 'clsx';
import {
  Children,
  cloneElement,
  forwardRef,
  Fragment,
  type HTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ActionButton } from './ActionButton.js';
import { actionGroupHiddenItemCSS } from './ActionGroup.sty.js';
import { CollapsedActionGroup } from './CollapsedActionGroup.js';
import { ACTION_GROUP_GAP } from './constants.js';
import { ActionGroupContext } from './contexts/ActionGroupContext.js';
import { Group } from './Group.js';
import { NavItem } from './NavItem.js';
import { useContainerSize } from '../../../core/hooks/useContainerSize.js';
import { useMeasureChildren } from '../../../core/hooks/useMeasureChildren.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useRovingFocus } from '../../../core/hooks/useRovingFocus.js';
import { DataTestId } from '../../../core/types/data-props.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { WithChildren } from '../../../core/types/with-children.js';
import { mergeProps as _mergeProps } from '../../../core/utils/merge-props.js';
import { Divider } from '../../divider/Divider.js';
import { Flex, FlexOwnProps } from '../../flex/Flex.js';

const COMPONENT_NAME = 'ActionGroup';

// #region Typings
/** @internal */
export interface ActionGroupProps
  extends
    FlexOwnProps,
    HTMLAttributes<HTMLElement>,
    WithChildren,
    StylingProps,
    DataTestId {
  /**
   * Whether or not the ActionGroup is collapsible.
   * @defaultValue true
   */
  collapsible?: boolean;
  /**
   * Whether the items in the ActionGroup should have a roving focus applied.
   * @defaultValue true
   */
  rovingFocus?: boolean;
  /**
   * Whether to collapse starting from the beginning or end of the ActionGroup.
   * @defaultValue start
   */
  collapseFrom?: 'start' | 'end';
  /**
   * Icon used for the trigger that opens/closes the overlay when the ActionGroup is collapsed.
   */
  triggerIcon?: ReactElement;
  /**
   * Influences the spacings. Condensed is more compact.
   */
  density?: 'default' | 'condensed';
  /**
   * Handler called when the collapsed state changes.
   */
  onCollapsedChange?: (isCollapsed: boolean) => void;
  /**
   * Handler called when at least one item is moved into the menu.
   */
  onOverflowChange?: (isCollapsed: boolean) => void;
}

type SlotsType = {
  items: ReactNode[];
  menuItems: ReactNode[];
};
// #endregion

function itemIsPinned(child: ReactNode): boolean {
  return (
    isValidElement(child) &&
    typeof child.props === 'object' &&
    child.props !== null &&
    'pin' in child.props &&
    child.props.pin === true
  );
}

const ActionGroupRoot = forwardRef<HTMLDivElement, ActionGroupProps>(
  (props, forwardedRef) => {
    const {
      collapsible = true,
      collapseFrom = 'start',
      children,
      rovingFocus = true,
      triggerIcon,
      density = 'default',
      onCollapsedChange,
      onOverflowChange,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...restProps
    }: ActionGroupProps = props;
    const _containerRef = useRef<HTMLDivElement | null>(null);

    const { containerRef, measurements, childProps } = useMeasureChildren();

    // Sorted array of child nodes, including dividers (but not the menu trigger). Needed to access
    // the child node corresponding to the index of the childProps returned from the useMeasureChildren hook.
    const measuredChildren = useRef<
      Array<ReactNode | { props?: { pin: boolean } }>
    >([]);
    const { width } = useContainerSize(_containerRef);
    // We need to round up the width, otherwise we might run into an endless loop
    // with the ActionGroup constantly collapsing / expanding.
    const containerWidth = Math.round(width);

    const menuItems = useRef(new Map<string, boolean>());
    // Number of items that are pinned. Used to adjust when the onCollapsedChange callback is triggered.
    const pinnedItemsCount = useRef(0);

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const totalChildrenWidth = useMemo(() => {
      // Get total width of children.
      let childWidths = Object.values(measurements).reduce(
        (acc, value) => (acc += value?.width || 0),
        0,
      );
      // We also have to include the gaps the flex container is adding.
      childWidths += (childProps.length - 1) * ACTION_GROUP_GAP;

      return childWidths;
    }, [childProps, measurements]);

    const slots = useMemo(() => {
      const slots: SlotsType = { items: [], menuItems: [] };

      // Using React.Children.toArray and not React.Children.forEach to get the correct index for each child.
      // If an item is conditionally rendered in the ActionGroup, it would still be iterated over using forEach,
      // even if it ends up not being rendered in the DOM.
      // Converting the children to an array already removes children that will not be rendered.
      const childrenArray = Children.toArray(children);

      // As soon as we have sufficient data from the measurement hook,
      // determine visible and hidden action group items.
      if (
        collapsible &&
        containerWidth > 0 &&
        childProps.length > 0 &&
        Object.keys(measurements).length === childProps.length
      ) {
        const childKeys = childProps.map((props) => props['data-id']);

        // Index of the next child to remove if there is not enough space.
        // Currently collapsing the actions starting on the start.
        let childIndex = collapseFrom === 'start' ? 0 : childProps.length - 1;
        // Clear all entries so that action group items will show up again when expanding the container.
        menuItems.current.clear();

        let childWidths = totalChildrenWidth;
        while (
          childWidths > containerWidth &&
          ((collapseFrom === 'end' && childIndex >= 0) ||
            (collapseFrom === 'start' && childIndex < childProps.length))
        ) {
          const childNode =
            measuredChildren.current[
              // The childProps array also includes props for the menu trigger, which is not included in the
              // measured children array. If collapsing from the end, the index needs to be shifted back by 1.
              childIndex + (isOverflowing && collapseFrom === 'end' ? -1 : 0)
            ];

          // If an item is pinned, we don't push it into the menu.
          if (isValidElement(childNode) && itemIsPinned(childNode)) {
            // Intentionally left empty
          } else {
            let childKeyIndex = childIndex;
            if (isOverflowing) {
              // If an item is already overflowing, we need to shift the index by 1 to not
              // include the menu trigger in the calculation since it will never be hidden.
              const collapseFromOffset = collapseFrom === 'start' ? 1 : -1;
              childKeyIndex = childKeyIndex + collapseFromOffset;
            }
            const key = childKeys[childKeyIndex];
            if (key && measurements[key] !== undefined) {
              // Add item to the list of items rendered in the menu.
              menuItems.current.set(key, true);
              // Subtract freed up space and check again, if there's sufficient space to render the remaining children.
              childWidths -= (measurements[key]?.width || 0) + ACTION_GROUP_GAP;
            }
          }

          childIndex += collapseFrom === 'start' ? 1 : -1;
        }
      }

      let groupCount = 0;
      let pinnedItems = 0;
      measuredChildren.current = [];

      for (const [index, child] of childrenArray.entries()) {
        if (!isValidElement(child)) {
          continue;
        }

        // The key applied to the children so react is not removing the nodes when adding the menu trigger.
        // This would cause the mutation observer of the measurement hook to trigger and mess up the calculations.
        // Applying a consistent child key resolves that issue.
        const childKey = index + groupCount;
        // The index of the child used to apply the correct childProps returned from the measurement hook.
        // If collapsing from the end and an overflow is already happening, we need to shift by 1 to account
        // for the menu trigger that is then also included in the childProps array.
        const childIndex =
          childKey + (isOverflowing && collapseFrom === 'start' ? 1 : 0);

        const isItemOverflowing = menuItems.current.get(
          childProps[childIndex]?.['data-id'] || '',
        );

        const clonedChild = cloneElement(
          child,
          _mergeProps(child.props ?? {}, childProps[childIndex], {
            key: childKey,
            className: clsx(props.className, {
              [actionGroupHiddenItemCSS]: isItemOverflowing,
            }),
          }),
        );

        // TODO: If the item before the group is not also a group, we should also add a divider there.
        // Add a divider for each group rendered in the ActionGroup (except the last one).
        if (child.type === Group) {
          // If the item after the divider is overflowing, the divider needs to be hidden.
          const isNextItemOverflowing = menuItems.current.get(
            childProps[childIndex + 2]?.['data-id'] || '',
          );
          measuredChildren.current.push(clonedChild);

          slots.items.push(
            <Fragment key={`fragment-${childKey}`}>
              {clonedChild}
              {index !== childrenArray.length - 1 && (
                <Divider
                  orientation="vertical"
                  flexItem={false}
                  style={{
                    height: 20,
                  }}
                  className={clsx({
                    [actionGroupHiddenItemCSS]:
                      !!isItemOverflowing || !!isNextItemOverflowing,
                  })}
                  {...childProps[childIndex + 1]}
                  aria-hidden
                  key={childKey + 1}
                />
              )}
            </Fragment>,
          );

          if (itemIsPinned(child)) {
            measuredChildren.current.push({ props: { pin: true } });
            pinnedItems += 1;
          } else {
            measuredChildren.current.push({});
          }

          groupCount += 1;
        } else {
          measuredChildren.current.push(clonedChild);
          slots.items.push(clonedChild);

          if (itemIsPinned(child)) {
            pinnedItems += 1;
          }
        }

        if (isItemOverflowing) {
          slots.menuItems.push(child);
        }
      }

      pinnedItemsCount.current = pinnedItems;

      setIsOverflowing(slots.menuItems.length > 0);
      setIsCollapsed(
        slots.menuItems.length + pinnedItemsCount.current ===
          slots.items.length,
      );

      return slots;
      // Excluding state 'isOverflowing' to prevent an additional re-render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      measurements,
      childProps,
      children,
      collapseFrom,
      collapsible,
      totalChildrenWidth,
      containerWidth,
    ]);

    const { containerRef: rovingContainerRef } = useRovingFocus<HTMLDivElement>(
      {
        // FIXME: Follow-up for detecting the initial index already, when it is initially undefined based on
        // either tab-index attributes or the first focusable element. This will solve the problem for
        // everyone and will not overcomplicate the logic. ADES-2260
        initialIndex: undefined,
        direction: 'horizontal',
        focusableChildrenSelector:
          ':scope a, :scope button, :scope [type="button"]',
      },
    );

    const rootRef = useMergeRefs([
      forwardedRef,
      ...(rovingFocus ? [rovingContainerRef] : []),
    ]);
    const collapsibleRootRef = useMergeRefs([
      rootRef,
      containerRef,
      _containerRef,
    ]);

    // Trigger onOverflowChange callback whenever the isOverflowing state changes.
    useEffect(() => {
      onOverflowChange?.(isOverflowing);
    }, [isOverflowing, onOverflowChange]);

    // Trigger onCollapsedChange callback whenever the isCollapsed state changes.
    useEffect(() => {
      onCollapsedChange?.(isCollapsed);
    }, [isCollapsed, onCollapsedChange]);

    return (
      <Flex
        data-actiongroup
        alignItems="center"
        data-testid={dataTestId}
        ref={collapsible ? collapsibleRootRef : rootRef}
        gap={ACTION_GROUP_GAP}
        className={consumerClassName}
        style={{ minWidth: !collapsible ? 0 : undefined, ...consumerStyle }}
        {...restProps}
        data-dt-component={COMPONENT_NAME}
      >
        <ActionGroupContext.Provider
          value={{
            isCollapsed: false,
          }}
        >
          {collapseFrom === 'start' && slots?.menuItems.length > 0 && (
            <CollapsedActionGroup
              triggerIcon={triggerIcon}
              childProps={childProps[0]}
              density={density}
            >
              {slots?.menuItems}
            </CollapsedActionGroup>
          )}
          {slots?.items}
          {collapseFrom === 'end' && slots?.menuItems.length > 0 && (
            <CollapsedActionGroup
              triggerIcon={triggerIcon}
              childProps={childProps[childProps.length - 1]}
              density={density}
            >
              {slots?.menuItems}
            </CollapsedActionGroup>
          )}
        </ActionGroupContext.Provider>
      </Flex>
    );
  },
);

(
  ActionGroupRoot as typeof ActionGroupRoot & { displayName: string }
).displayName = 'ActionGroup';

/**
 * The `ActionGroup` groups related actions in a container. It takes care of
 * collapsing the actions into an overlay if there is insufficient space for
 * rendering all the actions next to each other. It also applies a roving focus
 * to the actions.
 * @internal
 */
export const ActionGroup = Object.assign(ActionGroupRoot, {
  ActionButton,
  Group,
  NavItem,
});
